<script setup>
import { computed, ref, onMounted } from 'vue'
import { getRandomEventTypes, getEventIcon } from '~/data/events'

const props = defineProps({
  locationName: {
    type: String,
    required: true
  },
  eventType: {
    type: String,
    default: 'Veranstaltungen'
  },
  description: {
    type: String,
    default: ''
  }
})

// Generiere eine Beschreibung, falls keine angegeben wurde
const defaultDescription = computed(() => {
  return `Unsere Fotoboxen sind in ganz ${props.locationName} und Umgebung verfügbar. Wir liefern, bauen auf und holen wieder ab – für Ihre perfekte Veranstaltung. Egal ob in einem Restaurant, Hotel, einer Eventlocation oder bei Ihnen zuhause - wir sorgen dafür, dass Ihre Gäste unvergessliche Momente festhalten können.`
})

// Verwende die bereitgestellte Beschreibung oder die Standard-Beschreibung
const displayDescription = computed(() => {
  return props.description || defaultDescription.value
})

// Generische Service-Highlights, die für jede Location gelten
const serviceHighlights = [
  'Kostenlose Anlieferung und Abholung',
  'Auf Wunsch professioneller Betreuer',
  'Technik-Support während der gesamten Veranstaltung',
  'Flexible Auf- und Abbauzeiten nach Absprache',
  'Individuelle Anpassungen an Ihre Location'
]

// Generische Vorteile der Fotobox für den jeweiligen Bezirk
const localBenefits = computed(() => [
  `Perfekte Unterhaltung für Ihre Gäste in ${props.locationName}`,
  `Individuelles Layout im Stil Ihrer Veranstaltung`,
  `Unbegrenzte digitale Fotos für alle Gäste`,
  `Flexible Aufstellmöglichkeiten passend zu Ihrer Location`,
  `Schneller Service in ganz ${props.locationName}`
])

// Zufällige Einsatzgebiete
const randomEvents = ref([])

onMounted(() => {
  // Lade 6 zufällige Eventtypen beim Mounting der Komponente
  randomEvents.value = getRandomEventTypes(6)
})

// Helfer-Funktion zum Gruppieren der Einsatzgebiete in Reihen (2 Elemente pro Reihe)
const eventRows = computed(() => {
  const rows = []
  for (let i = 0; i < randomEvents.value.length; i += 2) {
    rows.push(randomEvents.value.slice(i, i + 2))
  }
  return rows
})
</script>

<template>
  <section class="py-16 bg-white">
    <div class="container mx-auto px-4">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <h2 class="heading-2 mb-4">Fotobox für {{ eventType }} in {{ locationName }}</h2>
          <p class="text-gray-600 mb-6">{{ displayDescription }}</p>
          
          <h3 class="text-xl font-semibold mb-3">Unsere Services in {{ locationName }}:</h3>
          <ul class="space-y-2 mb-6">
            <li v-for="service in serviceHighlights" :key="service" class="flex items-start">
              <span class="text-primary mr-2">•</span>
              <span>{{ service }}</span>
            </li>
          </ul>
          
          <h3 class="text-xl font-semibold mb-3">Ihre Vorteile in {{ locationName }}:</h3>
          <ul class="space-y-2 mb-6">
            <li v-for="benefit in localBenefits" :key="benefit" class="flex items-start">
              <span class="text-primary mr-2">✓</span>
              <span>{{ benefit }}</span>
            </li>
          </ul>
          
          <p class="text-gray-600">Kontaktieren Sie uns für ein individuelles Angebot für Ihre Veranstaltung in {{ locationName }}!</p>
        </div>
        
        <div class="bg-gray-50 rounded-lg p-6 shadow-sm">
          <div class="mb-4">
            <h3 class="text-xl font-semibold mb-3">Einsatzbereiche unserer Fotobox in {{ locationName }}</h3>
            <p class="text-gray-600 mb-4">Unsere Fotoboxen sind vielseitig einsetzbar und passen sich perfekt an Ihre Veranstaltung an:</p>
          </div>
          
          <div class="space-y-4">
            <div v-for="row in eventRows" :key="row[0].name" class="grid grid-cols-2 gap-4">
              <div v-for="event in row" :key="event.name" class="bg-white p-4 rounded shadow-sm">
                <div class="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                  <div class="text-primary" v-html="getEventIcon(event.icon)" />
                </div>
                <h4 class="font-medium mb-1">{{ event.name }}</h4>
                <p class="text-sm text-gray-600">{{ event.description }}</p>
              </div>
            </div>
          </div>
          
          <div class="mt-6">
            <div class="border-t border-gray-200 pt-4">
              <h4 class="font-medium mb-2">Flexibel einsetzbar in {{ locationName }}</h4>
              <p class="text-sm text-gray-600">
                Unsere Fotoboxen sind kompakt und flexibel - wir finden auch in kleineren Locations den perfekten Platz.
                Bei Bedarf beraten wir Sie gerne zu Aufstellmöglichkeiten an Ihrem Veranstaltungsort.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>